import { useState, useCallback, useRef } from 'react'
import type { Toast, ToastType } from '@/types'

// Module-level singleton state for toast accessibility from anywhere
let _toastStore: ToastStore | null = null
let _listeners: Array<(toasts: Toast[]) => void> = []

interface ToastStore {
  toasts: Toast[]
  add: (message: string, type: ToastType, duration?: number) => string
  remove: (id: string) => void
  clear: () => void
}

function generateId(): string {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36)
}

function createToastStore(): ToastStore {
  let toasts: Toast[] = []
  const timers = new Map<string, ReturnType<typeof setTimeout>>()

  function notify() {
    _listeners.forEach((listener) => listener([...toasts]))
  }

  return {
    get toasts() {
      return toasts
    },

    add(message: string, type: ToastType = 'info', duration = 3500): string {
      const id = generateId()
      const toast: Toast = {
        id,
        message,
        type,
        duration,
        createdAt: Date.now(),
      }

      toasts = [toast, ...toasts].slice(0, 5) // max 5 toasts
      notify()

      if (duration > 0) {
        const timer = setTimeout(() => {
          this.remove(id)
        }, duration)
        timers.set(id, timer)
      }

      return id
    },

    remove(id: string) {
      const timer = timers.get(id)
      if (timer) {
        clearTimeout(timer)
        timers.delete(id)
      }
      toasts = toasts.filter((t) => t.id !== id)
      notify()
    },

    clear() {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
      toasts = []
      notify()
    },
  }
}

function getStore(): ToastStore {
  if (!_toastStore) {
    _toastStore = createToastStore()
  }
  return _toastStore
}

// Public singleton API for use outside of React components
export const toastService = {
  success: (message: string, duration?: number) =>
    getStore().add(message, 'success', duration),
  error: (message: string, duration?: number) =>
    getStore().add(message, 'error', duration ?? 5000),
  warning: (message: string, duration?: number) =>
    getStore().add(message, 'warning', duration),
  info: (message: string, duration?: number) =>
    getStore().add(message, 'info', duration),
  remove: (id: string) => getStore().remove(id),
  clear: () => getStore().clear(),
}

// React hook for components
export function useToast() {
  const store = getStore()
  const [toasts, setToasts] = useState<Toast[]>([...store.toasts])
  const listenerRef = useRef<(toasts: Toast[]) => void>()

  // Register listener on mount, unregister on unmount
  if (!listenerRef.current) {
    listenerRef.current = (updated: Toast[]) => setToasts(updated)
    _listeners.push(listenerRef.current)
  }

  // Clean up on unmount via a stable callback
  const mounted = useRef(true)
  if (!mounted.current) {
    _listeners = _listeners.filter((l) => l !== listenerRef.current)
  }

  const toast = useCallback(
    (message: string, type: ToastType = 'info', duration = 3500): string => {
      return store.add(message, type, duration)
    },
    [store]
  )

  const removeToast = useCallback(
    (id: string) => {
      store.remove(id)
    },
    [store]
  )

  const success = useCallback(
    (message: string, duration?: number) => store.add(message, 'success', duration),
    [store]
  )

  const error = useCallback(
    (message: string, duration?: number) => store.add(message, 'error', duration ?? 5000),
    [store]
  )

  const warning = useCallback(
    (message: string, duration?: number) => store.add(message, 'warning', duration),
    [store]
  )

  const info = useCallback(
    (message: string, duration?: number) => store.add(message, 'info', duration),
    [store]
  )

  return {
    toasts,
    toast,
    removeToast,
    success,
    error,
    warning,
    info,
  }
}

// Cleanup helper for component unmount
export function useToastCleanup() {
  const listenerRef = useRef<((toasts: Toast[]) => void) | null>(null)
  return () => {
    if (listenerRef.current) {
      _listeners = _listeners.filter((l) => l !== listenerRef.current)
    }
  }
}
